import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { api } from './api';
const Sidebar = lazy(() => import('./components/Sidebar').then((m) => ({ default: m.Sidebar })));
const MobileNav = lazy(() => import('./components/MobileNav').then((m) => ({ default: m.MobileNav })));
const InstallPrompt = lazy(() => import('./components/InstallPrompt').then((m) => ({ default: m.InstallPrompt })));
const Walkthrough = lazy(() => import('./components/Walkthrough').then((m) => ({ default: m.Walkthrough })));
const Auth = lazy(() => import('./pages/Auth'));
const Poster = lazy(() => import('./pages/Poster'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Reviews = lazy(() => import('./pages/Reviews'));
const Grow = lazy(() => import('./pages/Grow'));
const AddReview = lazy(() => import('./pages/AddReview'));
const Categories = lazy(() => import('./pages/Categories'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
import PublicReview from './pages/PublicReview';
import './styles.css';
import { BrandLockup } from './components/Logo';

function Shell({ user, setUser, onLogout: logout, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="shell">
      {user && !user.onboarding_done && <Walkthrough onDone={() => setUser({ ...user, onboarding_done: true })} />}
      <Sidebar open={open} close={() => setOpen(false)} onLogout={logout} />
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <main className="main">
        <div className="mobile-bar">
          <BrandLockup size={30} />
          <button className="icon-btn" onClick={() => setOpen(true)} data-testid="open-sidebar-button"><Menu size={22} /></button>
        </div>
        {children}
      </main>
      <MobileNav />
      <InstallPrompt />
    </div>
  );
}

function Router() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const returningFromGoogle = location.hash?.includes('session_id=');

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) { setChecking(false); return; }
    if (window.location.pathname.startsWith('/r/')) { setUser(false); setChecking(false); return; }
    api.get('/auth/me').then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  if (returningFromGoogle) return <Suspense fallback={<div className="loading-screen">Rankgate GMB</div>}><AuthCallback setUser={setUser} /></Suspense>;

  const loading = <div className="loading-screen">Rankgate GMB</div>;
  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
    toast.success('Signed out');
  };
  const owner = (element) => (checking ? loading : user ? <Shell user={user} setUser={setUser} onLogout={logout}>{element}</Shell> : <Navigate to="/" replace />);
  const bare = (element) => (checking ? loading : user ? element : <Navigate to="/" replace />);

  return (
    <Suspense fallback={loading}>
    <Routes>
      <Route path="/r/:slug" element={<PublicReview />} />
      <Route path="/reset-password" element={<ResetPassword setUser={setUser} />} />
      <Route path="/" element={checking ? loading : user ? <Navigate to="/reviews" replace /> : <Auth setUser={setUser} />} />
      <Route path="/auth" element={<Navigate to="/" replace />} />
      <Route path="/poster" element={bare(<Poster />)} />
      <Route path="/reviews" element={owner(<Reviews />)} />
      <Route path="/reviews/new" element={owner(<AddReview />)} />
      <Route path="/grow" element={owner(<Grow />)} />
      <Route path="/categories" element={owner(<Categories />)} />
      <Route path="/settings" element={owner(<Settings />)} />
      <Route path="/profile" element={owner(<Profile user={user} onLogout={logout} />)} />
      <Route path="/change-password" element={<Navigate to="/profile" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Router />
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}
